import axios from 'axios';
import { signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth';
import { getFirebaseAuth, getGoogleProvider } from '../config/firebase';

// Helper to ensure no double /api
const getApiBaseUrl = () => {
  let baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : 'https://api.niswainfotech.com/api');
  // Remove trailing /api if it exists, then add it once
  baseUrl = baseUrl.replace(/\/api\/?$/, '');
  return `${baseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Firebase Authentication Service
export const firebaseAuthService = {
  signInWithGoogle: async () => {
    try {
      const auth = getFirebaseAuth();
      const provider = getGoogleProvider();
      
      if (!auth || !provider) {
        throw new Error('Firebase not initialized');
      }

      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      
      const response = await axios.post(`${API_BASE_URL}/firebase/auth/google`, {
        idToken
      });

      if (response.data.success) {
        return {
          success: true,
          user: response.data.data.user,
          token: response.data.data.token
        };
      } else {
        throw new Error(response.data.message || 'Authentication failed');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      return { success: false, error: error.message };
    }
  },

  signOut: async () => {
    try {
      const auth = getFirebaseAuth();
      if (auth) {
        await firebaseSignOut(auth);
      }
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    }
  }
};

// reCAPTCHA Service
export const recaptchaService = {
  verifyToken: async (token, remoteIp = null) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/recaptcha/verify`, {
        token,
        remoteIp
      });
      return response.data;
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'reCAPTCHA verification failed'
      };
    }
  },

  getConfig: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/recaptcha/config`);
      return response.data;
    } catch (error) {
      console.error('reCAPTCHA config error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get reCAPTCHA config'
      };
    }
  },

  getPageConfig: async (page) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/recaptcha/page-settings/${page}`);
      return response.data;
    } catch (error) {
      console.error('reCAPTCHA page config error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get reCAPTCHA page config'
      };
    }
  }
};

// Google Analytics Service
export const googleAnalyticsService = {
  trackPageView: async (page, title = null, clientId = null) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/analytics/pageview`, {
        page,
        title,
        clientId
      });
      return response.data;
    } catch (error) {
      // Only log errors in development or if it's not a 404 (which indicates missing backend)
      if (process.env.NODE_ENV === 'development' && error.response?.status !== 404) {
        console.error('Google Analytics pageview error:', error);
      }
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to track page view'
      };
    }
  },

  trackEvent: async (eventName, parameters = {}, clientId = null) => {
    try {
      // Sanitize parameters to remove any circular references
      const sanitizedParameters = {};
      for (const [key, value] of Object.entries(parameters)) {
        // Only include primitive values and simple objects
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
          sanitizedParameters[key] = value;
        } else if (typeof value === 'object' && value !== null) {
          // For objects, try to extract only primitive values
          const sanitizedObject = {};
          for (const [objKey, objValue] of Object.entries(value)) {
            if (typeof objValue === 'string' || typeof objValue === 'number' || typeof objValue === 'boolean' || objValue === null) {
              sanitizedObject[objKey] = objValue;
            }
          }
          sanitizedParameters[key] = sanitizedObject;
        }
      }

      const response = await axios.post(`${API_BASE_URL}/analytics/event`, {
        eventName,
        parameters: sanitizedParameters,
        clientId
      });
      return response.data;
    } catch (error) {
      // Only log errors in development or if it's not a 404 (which indicates missing backend)
      if (process.env.NODE_ENV === 'development' && error.response?.status !== 404) {
        console.error('Google Analytics event error:', error);
      }
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to track event'
      };
    }
  }
};

// Google Maps Service
export const googleMapsService = {
  geocodeAddress: async (address) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/maps/geocode`, {
        params: { address }
      });
      return response.data;
    } catch (error) {
      console.error('Google Maps geocoding error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to geocode address'
      };
    }
  }
};

export default {
  firebaseAuthService,
  recaptchaService,
  googleAnalyticsService,
  googleMapsService
};
